import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  tasks: [],
  subtasks: [],
  tasksByGroupId: {},
  isSubtask: false,
  currentSubtask: null,
  commentDrafts: {},
  taskById: {},
};

const taskSlice = createSlice({
  name: "task",
  initialState,
  reducers: {
    setTasks: (state, action) => {
      state.tasks = action.payload.tasks;
    },
    addTask: (state, action) => {
      state.tasks.push(action.payload.task);
    },

    updateTask: (state, action) => {
      const updated = action.payload.task;

      // mantém compatibilidade com seu array antigo (se ainda usado em algum lugar)
      state.tasks = state.tasks.map(task =>
        task._id === updated._id ? updated : task
      );

      // garante atualização imediata para quem consome taskById
      if (updated?._id) {
        state.taskById[updated._id] = updated;
      }
    },

    removeTask: (state, action) => {
      state.tasks = state.tasks.filter(task => task._id !== action.payload.taskId);
      // Também remover subtasks associadas à task excluída
      state.subtasks = state.subtasks.filter(subtask => subtask.parentId !== action.payload.taskId);
    },

    // Define um conjunto de subtasks associadas a um parentId
    setSubtasks: (state, action) => {
      const { parentId, subtasks } = action.payload;
      const existingSubtasks = state.subtasks.find(item => item.parentId === parentId);

      if (existingSubtasks) {
        existingSubtasks.subtasks = subtasks;
      } else {
        state.subtasks.push({ parentId, subtasks });
      }
    },

    setIsSubtask: (state, action) => {
      state.isSubtask = action.payload.isSubtask;
    },

    setCurrentSubtask: (state, action) => {
      state.currentSubtask = action.payload.currentSubtask;
    },

    setGroupTasks: (state, action) => {
      const { groupId, tasks } = action.payload;

      // 1) upsert no cache por id
      for (const t of (tasks || [])) {
        if (t?._id) state.taskById[t._id] = t;
      }

      // 2) no grupo fica só a ordem/ids (sem objetos pesados)
      const ids = (tasks || []).map(t => t?._id).filter(Boolean);

      state.tasksByGroupId = {
        ...state.tasksByGroupId,
        [groupId]: ids,
      };
    },
    setTasksByGroupId: (state, action) => {
      state.tasksByGroupId = action.payload;
    },
    clearAllGroupTasks: (state) => {
      state.tasksByGroupId = {}; // limpa tudo
    },
    clearGroupTasks: (state, action) => {
      const groupId = action.payload.groupId;
      delete state.tasksByGroupId[groupId];
    },

    updateGroupTask: (state, action) => {
      const { groupId, task } = action.payload;
      if (!task?._id) return;

      // cache
      state.taskById[task._id] = task;

      // ids do grupo: não precisa mapear nada
      if (!state.tasksByGroupId[groupId]) state.tasksByGroupId[groupId] = [];
      if (!state.tasksByGroupId[groupId].includes(task._id)) {
        state.tasksByGroupId[groupId].push(task._id);
      }
    },

    addGroupTask: (state, action) => {
      const { groupId, task } = action.payload;
      if (!task?._id || !groupId) return;

      state.taskById[task._id] = task;

      const arr = state.tasksByGroupId[groupId] || [];
      if (!arr.includes(task._id)) state.tasksByGroupId[groupId] = [...arr, task._id];
    },

    removeGroupTask: (state, action) => {
      const { groupId, taskId } = action.payload;
      if (!groupId || !taskId) return;

      const arr = state.tasksByGroupId[groupId] || [];
      state.tasksByGroupId[groupId] = arr.filter(id => id !== taskId);
    },

    setCommentDraft: (state, action) => {
      const { key, content } = action.payload;
      state.commentDrafts[key] = content;
    },

    clearCommentDraft: (state, action) => {
      delete state.commentDrafts[action.payload.key];
    },

    removeTasksFromStore: (state, action) => {
      const { groupId, taskIds } = action.payload;
      if (!Array.isArray(taskIds) || taskIds.length === 0) return;

      const idSet = new Set(taskIds.map(String));

      for (const id of idSet) {
        delete state.taskById?.[id];
      }

      const list = state.tasksByGroupId?.[groupId];
      if (Array.isArray(list)) {
        state.tasksByGroupId[groupId] = list.filter(id => !idSet.has(String(id)));
      }

      if (Array.isArray(state.tasks)) {
        state.tasks = state.tasks.filter(t => !idSet.has(String(t?._id || t)));
      }
      if (Array.isArray(state.subtasks)) {
        state.subtasks = state.subtasks.filter(t => !idSet.has(String(t?._id || t)));
      }
    },

    moveTaskBetweenGroupsOptimistic: (state, action) => {
      const { task, fromGroupId, toGroupId } = action.payload;
      if (!task?._id) return;

      state.taskById[task._id] = task;

      state.tasksByGroupId[fromGroupId] =
        (state.tasksByGroupId[fromGroupId] || []).filter(id => id !== task._id);

      const toArr = state.tasksByGroupId[toGroupId] || [];
      state.tasksByGroupId[toGroupId] = toArr.includes(task._id) ? toArr : [task._id, ...toArr];
    },

    setGroupTaskIds: (state, action) => {
      const { groupId, taskIds } = action.payload;
      state.tasksByGroupId = { ...state.tasksByGroupId, [groupId]: taskIds || [] };
    },

    resetTask: () => initialState,
  },
});

export const {
  setTasks,
  addTask,
  updateTask,
  removeTask,
  setSubtasks,
  addSubtask,
  setIsSubtask,
  setCurrentSubtask,
  setGroupTasks,
  clearGroupTasks,
  updateGroupTask,
  addGroupTask,
  removeGroupTask,
  clearAllGroupTasks,
  setCommentDraft,
  clearCommentDraft,
  resetTask,
  setTasksByGroupId,
  removeTasksFromStore,
  moveTaskBetweenGroupsOptimistic,
  setGroupTaskIds,
} = taskSlice.actions;


export default taskSlice.reducer;
